import axiosInstance from "../config/axios";
import API_ENDPOINTS from "../config/endpoints";
import { titleMatchesSlug, normalizeSlug } from "../../utils/slugUtils";
import { matchesCategory } from "../../utils/helper";

// Helper function to build full image URL from relative path
const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If relative path, prepend base URL if available
  const imageBase = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || '';
  if (imageBase && !imagePath.startsWith('/')) {
    return `${imageBase}/${imagePath}`;
  } else if (imageBase) {
    return `${imageBase}${imagePath}`;
  }
  return imagePath;
};

// Helper function to transform API product to component format
const transformProduct = (product, priorityOverride = null) => {
  // Use priorityOverride if provided (from CampaignProducts), otherwise use original priortyProduct
  const priority = priorityOverride !== null ? priorityOverride : product.priortyProduct;
  return {
    id: product.id,
    title: product.name || '',
    image: buildImageUrl(product.image || ''),
    price: product.price || 0,
    quantity: product.category || '',
    goal: parseInt(product.qty) || 5000,
    obtained: product.quantity_obtained || 0,
    status: "active",
    cardSize: priority === 1 ? 1 : 0,
  };
};

// Helper function to transform API project description to component format
const transformProject = (project) => {
  let imageUrl = '';
  if (Array.isArray(project.images) && project.images.length > 0) {
    imageUrl = typeof project.images[0] === 'string' ? project.images[0] : project.images[0];
  } else if (typeof project.images === 'string') {
    imageUrl = project.images;
  }
  return {
    id: project.id,
    image: buildImageUrl(imageUrl),
    title: project.title || '',
    tag: project.tag || '',
    description: project.description || '',
    bulletPoints: project.bulletPoints || [],
  };
};

// Helper function to transform API update to component format
const transformUpdate = (update) => {
  // Handle images - can be array of strings, array of objects with url, or empty
  let images = [];
  if (Array.isArray(update.images) && update.images.length > 0) {
    images = update.images.map(img => {
      if (typeof img === 'string') {
        // Direct URL string
        return buildImageUrl(img);
      } else if (img && typeof img === 'object') {
        // Handle object with url property (e.g., { id: 123, url: "uploads/..." })
        if (img.url && typeof img.url === 'string') {
          return buildImageUrl(img.url);
        }
        // If object doesn't have url, try to use the object itself (shouldn't happen but fallback)
        return '';
      }
      return '';
    }).filter(img => img && img !== ''); // Remove empty strings
  }
  
  return {
    id: update.id,
    title: update.title || '',
    description: update.description || '',
    images: images,
    video_url: update.video_url || null,
    date: update.createdAt || update.updatedAt || new Date().toISOString(),
  };
};

// Helper function to transform API campaign to component format
export const transformCampaign = (apiCampaign) => {
  // Transform comments from API (filter by status === 1 for active comments)
  const comments = Array.isArray(apiCampaign.comments)
    ? apiCampaign.comments
        .filter(comment => comment.status === 1)
        .map(comment => ({
          id: comment.id,
          campaign_id: comment.campaign_id,
          title: comment.title,
          message: comment.message,
          status: comment.status,
          createdAt: comment.createdAt,
          updatedAt: comment.updatedAt,
          username: comment.username || 'Anonymous',
          profilepic: buildImageUrl(comment.profilepic),
        }))
    : [];
  
  // Static placeholder data for donors (not available in API yet)
  const staticDonors = [];

  // Transform story images if present
  let storyData = null;
  if (apiCampaign.story) {
    storyData = {
      ...apiCampaign.story,
      images: Array.isArray(apiCampaign.story.images)
        ? apiCampaign.story.images.map(img => {
            const imgUrl = typeof img === 'string' ? img : (img.url || img);
            return buildImageUrl(imgUrl);
          })
        : [],
    };
  }

  // Create a map of productIds to Priorityproduct from CampaignProducts (if exists)
  const campaignProductsMap = {};
  if (Array.isArray(apiCampaign.CampaignProducts) && apiCampaign.CampaignProducts.length > 0) {
    apiCampaign.CampaignProducts.forEach(cp => {
      if (cp.productIds !== undefined && cp.productIds !== null) {
        campaignProductsMap[cp.productIds] = cp.Priorityproduct !== undefined ? cp.Priorityproduct : null;
      }
    });
  }

  return {
    id: apiCampaign.id,
    image: buildImageUrl(apiCampaign.thumbnail_image || ''),
    bannerImage: buildImageUrl(apiCampaign.banner_image || ''),
    // Preserve raw description for detail views (life/medical/story)
    description: apiCampaign.description || '',
    // Existing summary title used in cards/progress components
    progressTitle: apiCampaign.description || '',
    taxBenefit: apiCampaign.exemption_tag || 'Tax Benefit',
    title: apiCampaign.title || '',
    location: apiCampaign.location || '',
    goalAmount: apiCampaign.goal || 0,
    raisedAmount: apiCampaign.raised || 0,
    backers:
      apiCampaign?.Totaldonors ??
      apiCampaign?.totalDonors ??
      apiCampaign?.total_donors ??
      apiCampaign?.backers ??
      0,
    category: apiCampaign.category || 'Urgent',
    campaign_type: apiCampaign.campaign_type || false,
    status: apiCampaign.status ?? 1, // Include status field, default to 1
    createdAt: apiCampaign.createdAt || apiCampaign.created_at || null,
    updatedAt: apiCampaign.updatedAt || apiCampaign.updated_at || null,
    // Hide flags from API
    hide_goal: apiCampaign.hide_goal ?? 0,
    hide_raised: apiCampaign.hide_raised ?? 0,
    hideAll: apiCampaign.hideAll ?? apiCampaign.hide_all ?? 0,
    // WishList flag (0 = show, 1 = hide)
    WishList: apiCampaign.WishList ?? apiCampaign.wishList ?? apiCampaign.wishlist ?? 0,
    // IsPrivate flag (0 = public, 1 = private - only visible to admins)
    IsPrivate: apiCampaign.IsPrivate ?? apiCampaign.isPrivate ?? apiCampaign.is_private ?? 0,
    // Closedcampaign flag (0 = active, 1 = closed)
    Closedcampaign: apiCampaign.Closedcampaign ?? apiCampaign.closedcampaign ?? apiCampaign.closed_campaign ?? 0,
    // FeatureUrgentCampaign flag (0 = not featured, 1 = featured urgent campaign)
    FeatureUrgentCampaign: apiCampaign.FeatureUrgentCampaign ?? apiCampaign.featureUrgentCampaign ?? apiCampaign.feature_urgent_campaign ?? 0,
    // Feature life campaign flag (0 = don't show on Life page, 1 = show in Medical Emergency section) – API may send featureLifecampaign
    featureLifeCampaign: apiCampaign.featureLifecampaign ?? apiCampaign.featureLifeCampaign ?? apiCampaign.FeatureLifeCampaign ?? apiCampaign.feature_life_campaign ?? 0,
    // Feature medical campaign – if 1 show on Life page only; if 0 do not show on Life page
    featuremedicalCampaign: apiCampaign.featuremedicalCampaign ?? apiCampaign.featureMedicalCampaign ?? apiCampaign.FeaturemedicalCampaign ?? apiCampaign.feature_medical_campaign ?? 0,
    // Whether donors may give monthly to this campaign. transformCampaign is a
    // whitelist, and getCampaignBySlug falls back to a transformed list when the
    // direct slug lookup misses — without this line the flag would be dropped on
    // that path and the box would offer monthly on a one-time-only campaign.
    // Default 1: absent means allowed.
    allow_monthly_donation: apiCampaign.allow_monthly_donation ?? 1,
    // Feature recent campaign – if 1 show in Recent Campaigns section only; if 0 exclude from that section
    featureRecentCampaign:
      apiCampaign.featureRecentCampaign ??
      apiCampaign.feature_recent_campaign ??
      apiCampaign.FeatureRecentCampaign ??
      0,
    // Medical Emergency (Life page): 1 = eligible for that strip; 0 = exclude
    latestLifeCampaign:
      apiCampaign.latestLifeCampaign ??
      apiCampaign.latest_life_campaign ??
      apiCampaign.LatestLifeCampaign ??
      0,
    // isSearch (0 = hide from header search, 1 = show in header search)
    isSearch: apiCampaign.isSearch ?? apiCampaign.is_search ?? 0,
    // Redirects data
    redirects: Array.isArray(apiCampaign.redirects) ? apiCampaign.redirects : [],
    // Products - use CampaignProducts priority if available, otherwise use original priortyProduct
    products: Array.isArray(apiCampaign.products) 
      ? apiCampaign.products.map(product => {
          // Check if this product has a priority override in CampaignProducts
          const priorityOverride = campaignProductsMap[product.id] !== undefined 
            ? campaignProductsMap[product.id] 
            : null;
          return transformProduct(product, priorityOverride);
        })
      : [],
    // Projects
    projects: Array.isArray(apiCampaign.projectDescriptions)
      ? apiCampaign.projectDescriptions
          .filter(p => p.status === 0 || p.status === false)
          .map(transformProject)
      : [],
    // Updates
    updates: Array.isArray(apiCampaign.updates)
      ? apiCampaign.updates.map(transformUpdate)
      : [],
    // Story (for medical campaigns)
    story: storyData,
    // Medical details (for medical campaigns)
    medical_details: apiCampaign.medical_details || null,
    // Documents
    documents: Array.isArray(apiCampaign.documents)
      ? apiCampaign.documents
          .filter(doc => !doc.isHideStatus)
          .map(doc => ({
            ...doc,
            url: buildImageUrl(doc.url),
          }))
      : [],
    // Social media links
    socialMediaLinks: apiCampaign.socialMediaLinks || null,
    // Comments from API
    comments: comments,
    // Static placeholders for donors
    donors: staticDonors,
  };
};

// Sample Campaign Service - Basic CRUD operations
export const getCampaigns = async (params = {}) => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_CAMPAIGNS, { params });
    // Axios interceptor returns response.data, so response is already the API response body
    // API returns { data: [...] }
    if (response && response.data && Array.isArray(response.data)) {
      // Filter out status 0 campaigns (only show active campaigns in listings)
      // Also filter out story campaigns (campaign_type === 1 or true) - only show normal campaigns
      // Transform campaigns and filter by status === 1 and campaign_type !== 1 (not story campaign)
      // Also exclude life campaigns (featureLifeCampaign === 1) so they don't appear in normal/story listings
      let filtered = response.data
        .map(transformCampaign)
        .filter(campaign => {
          // Only show active campaigns (status === 1)
          const isActive = campaign.status === 1;
          // Exclude story campaigns (campaign_type === 1 or true)
          const isNotStoryCampaign = campaign.campaign_type !== 1 && campaign.campaign_type !== true && campaign.campaign_type !== "1" && campaign.campaign_type !== "true";
          // Exclude life/medical campaigns from normal/story flows (show only on Life page)
          const isLifeOrMedicalCampaign = Number(campaign.featuremedicalCampaign) === 1 || Number(campaign.featureLifeCampaign) === 1;
          return isActive && isNotStoryCampaign && !isLifeOrMedicalCampaign;
        });

      // Enforce params client-side in case the backend ignores them
      if (params.FeatureUrgentCampaign !== undefined) {
        filtered = filtered.filter(c => Number(c.FeatureUrgentCampaign) === Number(params.FeatureUrgentCampaign));
      }
      if (params.featureRecentCampaign !== undefined) {
        filtered = filtered.filter(c => Number(c.featureRecentCampaign) === Number(params.featureRecentCampaign));
      }
      if (params.category !== undefined) {
        filtered = filtered.filter(c => matchesCategory(c.category, params.category));
      }
      if (params.limit) {
        // Sort newest first before slicing so we always get the most recent campaigns
        filtered = filtered.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0).getTime();
          const dateB = new Date(b.createdAt || b.created_at || 0).getTime();
          return dateB - dateA;
        });
        filtered = filtered.slice(0, Number(params.limit));
      }

      return {
        data: filtered,
      };
    }
    return response;
  } catch (error) {
    throw error;
  }
};

// Life campaigns: featuremedicalCampaign === 1 OR featureLifeCampaign === 1 (same rule as Life detail page), not closed, active.
// Sorted by updated date, latest first. Max count is applied in components after privacy filter.
export const getLifeCampaigns = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_CAMPAIGNS);
    if (response && response.data && Array.isArray(response.data)) {
      const transformedCampaigns = response.data
        .map(transformCampaign)
        .filter(campaign => {
          const isActive = campaign.status === 1;
          const isNotClosed = Number(campaign.Closedcampaign) !== 1;
          const isLife =
            Number(campaign.featuremedicalCampaign) === 1 ||
            Number(campaign.featureLifeCampaign) === 1;
          return isActive && isNotClosed && isLife;
        })
        .sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return dateB - dateA;
        });

      return {
        data: transformedCampaigns,
      };
    }
    return response;
  } catch (error) {
    throw error;
  }
};

export const getUrgentLifeCampaigns = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_SECTION_LIFE_URGENT);
    const raw = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    const limit = response?.limit_count ?? response?.section?.limit_count ?? 3;
    return { data: raw.map(transformCampaign), limit };
  } catch (error) {
    throw error;
  }
};

export const getRecentCampaigns = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_SECTION_RECENT_CAMPAIGNS);
    const raw = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    const limit = response?.limit_count ?? response?.section?.limit_count ?? 3;
    return { data: raw.map(transformCampaign), limit };
  } catch (error) {
    throw error;
  }
};

export const getHomeCategoryCampaigns = async (category) => {
  try {
    const params = category ? { category } : {};
    const response = await axiosInstance.get(API_ENDPOINTS.GET_SECTION_HOME_CATEGORIES, { params });
    const raw = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    return { data: raw.map(transformCampaign) };
  } catch (error) {
    throw error;
  }
};

// Derives section key from category name: "Disaster Relief" → home_disaster_relief
const toCategoryKey = (category) =>
  `home_${category.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;

export const getCategorySection = async (category, limit = 6) => {
  try {
    const key = toCategoryKey(category);
    const response = await axiosInstance.get(API_ENDPOINTS.GET_SECTION_ORDERING(key), {
      params: { category, limit },
    });
    const raw = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    // limit_count is admin-configured; fall back to passed limit
    const limitCount = response?.limit_count ?? response?.section?.limit_count ?? limit;
    return { data: raw.map(transformCampaign), limit: limitCount };
  } catch (error) {
    throw error;
  }
};

export const getCampaignById = async (id) => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_CAMPAIGN_BY_ID(id));
    // Axios interceptor returns response.data, so response is already the API response body
    // API might return { data: {...} } or directly {...}
    if (response && response.data) {
      return {
        data: transformCampaign(response.data),
      };
    } else if (response) {
      // If response is the campaign object directly
      return {
        data: transformCampaign(response),
      };
    }
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Query params for GET /campaigns/slug/:slug by route:
 * - /story/:slug → isStory=true
 * - /life/:slug → isStory=false
 * - /campaign/:slug → no query
 */
const getSlugApiQueryParams = (isStoryRoute, preferLifeCampaign) => {
  if (isStoryRoute === true) {
    return { isStory: true };
  }
  if (preferLifeCampaign === true) {
    return { isStory: false };
  }
  return undefined;
};

/** Full campaign detail from GET /campaigns/slug/:slug */
export const fetchCampaignDetailBySlug = async (
  slug,
  { isStoryRoute, preferLifeCampaign } = {}
) => {
  const trimmed = String(slug ?? "").trim();
  if (!trimmed) {
    throw new Error("Campaign not found");
  }

  const params = getSlugApiQueryParams(isStoryRoute, preferLifeCampaign);
  const response = await axiosInstance.get(API_ENDPOINTS.GET_CAMPAIGN_BY_SLUG(trimmed), {
    ...(params ? { params } : {}),
  });
  const raw = response?.data ?? response;

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return { data: transformCampaign(raw) };
  }

  throw new Error("Campaign not found");
};

const getRedirects = (campaign) =>
  campaign?.redirects && Array.isArray(campaign.redirects) ? campaign.redirects : [];

const redirectMatchesSlug = (redirect, normalized) => {
  const primaryUrl = redirect?.primary_url ? normalizeSlug(redirect.primary_url) : null;
  const secondaryUrl = redirect?.secondary_url ? normalizeSlug(redirect.secondary_url) : null;
  return primaryUrl === normalized || secondaryUrl === normalized;
};

const isLifeCampaign = (campaign) =>
  Number(campaign?.featuremedicalCampaign) === 1 ||
  Number(campaign?.featureLifeCampaign) === 1;

const isStoryCampaignEntity = (campaign) =>
  campaign?.campaign_type === 1 ||
  campaign?.campaign_type === true ||
  campaign?.campaign_type === "1" ||
  campaign?.campaign_type === "true";

const campaignMatchesRoute = (campaign, normalizedSlug, isStoryRoute, preferLifeCampaign) => {
  if (!campaign) return false;

  const redirects = getRedirects(campaign);
  const slugMatched =
    redirects.length > 0
      ? redirects.some((r) => redirectMatchesSlug(r, normalizedSlug))
      : campaign.title
        ? titleMatchesSlug(campaign.title, normalizedSlug)
        : false;

  if (!slugMatched) return false;

  if (typeof isStoryRoute !== "boolean") {
    return true;
  }

  if (isStoryRoute === true) {
    return redirects.some(
      (r) => redirectMatchesSlug(r, normalizedSlug) && r?.is_story_campaign === true
    );
  }

  const nonStoryMatch = redirects.some(
    (r) => redirectMatchesSlug(r, normalizedSlug) && r?.is_story_campaign !== true
  );
  if (!nonStoryMatch) return false;

  if (preferLifeCampaign) {
    return isLifeCampaign(campaign);
  }

  return !isLifeCampaign(campaign);
};

const getApiSlugForCampaign = (campaign, normalizedSlug, isStoryRoute, preferLifeCampaign) => {
  const redirects = getRedirects(campaign);
  const matchingRedirect = redirects.find((r) => {
    if (!redirectMatchesSlug(r, normalizedSlug)) return false;
    if (typeof isStoryRoute === "boolean") {
      return r?.is_story_campaign === isStoryRoute;
    }
    return true;
  });

  const primary = matchingRedirect?.primary_url ?? redirects[0]?.primary_url;
  return primary != null && String(primary).trim() !== "" ? String(primary).trim() : String(slug).trim();
};

const finalizeCampaignResult = (campaign, normalizedSlug, isStoryRoute) => {
  if (!campaign) return null;

  const data = { ...campaign };
  if (Array.isArray(data.redirects) && typeof isStoryRoute === "boolean") {
    const matchingRedirectIndex = data.redirects.findIndex(
      (r) => redirectMatchesSlug(r, normalizedSlug) && r?.is_story_campaign === isStoryRoute
    );
    if (matchingRedirectIndex > 0) {
      const reordered = [...data.redirects];
      const [picked] = reordered.splice(matchingRedirectIndex, 1);
      reordered.unshift(picked);
      data.redirects = reordered;
    }
  }
  return { data };
};

const fetchAllCampaignsForSlugResolution = async () => {
  const response = await axiosInstance.get(API_ENDPOINTS.GET_CAMPAIGNS);
  if (response?.data && Array.isArray(response.data)) {
    return response.data.map(transformCampaign);
  }
  return [];
};

const resolveCampaignFromList = (
  allCampaigns,
  normalizedSlug,
  slug,
  isStoryRoute,
  preferLifeCampaign
) => {
  const slugMatches = allCampaigns.filter((campaign) => {
    if (!campaign) return false;
    const redirects = getRedirects(campaign);
    if (redirects.length > 0) {
      return redirects.some((r) => redirectMatchesSlug(r, normalizedSlug));
    }
    if (campaign.title) {
      return titleMatchesSlug(campaign.title, slug);
    }
    return false;
  });

  let matchingCampaign = null;

  if (typeof isStoryRoute === "boolean") {
    if (isStoryRoute === true) {
      const storyRedirectMatches = slugMatches.filter((c) =>
        getRedirects(c).some(
          (r) => redirectMatchesSlug(r, normalizedSlug) && r?.is_story_campaign === true
        )
      );
      matchingCampaign =
        storyRedirectMatches.find((c) => isStoryCampaignEntity(c)) ??
        storyRedirectMatches.find((c) => !isLifeCampaign(c)) ??
        storyRedirectMatches[0] ??
        null;
    } else {
      const nonStoryMatches = slugMatches.filter((c) =>
        getRedirects(c).some(
          (r) => redirectMatchesSlug(r, normalizedSlug) && r?.is_story_campaign !== true
        )
      );
      if (preferLifeCampaign) {
        matchingCampaign = nonStoryMatches.find((c) => isLifeCampaign(c)) ?? null;
      } else {
        matchingCampaign =
          nonStoryMatches.find((c) => !isLifeCampaign(c)) ??
          nonStoryMatches[0] ??
          null;
      }
    }
  }

  if (!matchingCampaign && slugMatches.length > 0 && typeof isStoryRoute !== "boolean") {
    matchingCampaign = slugMatches[0];
  }

  return matchingCampaign;
};

/**
 * @param {string} slug - URL slug (primary, secondary, or mapping slug)
 * @param {boolean} [isStoryRoute] - true for /story/:slug → API ?isStory=true
 * @param {{ preferLifeCampaign?: boolean }} [options] - true for /life/:slug → API ?isStory=false
 */
export const getCampaignBySlug = async (slug, isStoryRoute = undefined, options = {}) => {
  try {
    const preferLifeCampaign = options?.preferLifeCampaign === true;
    const normalizedSlug = normalizeSlug(slug);
    const slugRouteOptions = { isStoryRoute, preferLifeCampaign };

    // 1) Primary: GET /campaigns/slug/{slug} (+ ?isStory for story/life routes)
    try {
      const direct = await fetchCampaignDetailBySlug(slug, slugRouteOptions);
      if (
        direct?.data &&
        campaignMatchesRoute(direct.data, normalizedSlug, isStoryRoute, preferLifeCampaign)
      ) {
        return finalizeCampaignResult(direct.data, normalizedSlug, isStoryRoute);
      }
    } catch {
      // Fall through to list-based disambiguation (shared slugs, mapping slugs, etc.)
    }

    const allCampaigns = await fetchAllCampaignsForSlugResolution();

    const allowCampaignMapping =
      (isStoryRoute === false || typeof isStoryRoute !== "boolean") &&
      !preferLifeCampaign;

    if (allowCampaignMapping) {
      const storyWithMapping = allCampaigns.find((campaign) =>
        getRedirects(campaign).some((r) => {
          const mappingSlugRaw = r?.campaignMapping?.redirectSlug;
          return mappingSlugRaw && normalizeSlug(mappingSlugRaw) === normalizedSlug;
        })
      );
      const mappedRedirect = getRedirects(storyWithMapping).find((r) => {
        const mappingSlugRaw = r?.campaignMapping?.redirectSlug;
        return mappingSlugRaw && normalizeSlug(mappingSlugRaw) === normalizedSlug;
      });
      const mappingSlugRaw = mappedRedirect?.campaignMapping?.redirectSlug;
      if (mappingSlugRaw) {
        const mapped = await fetchCampaignDetailBySlug(String(mappingSlugRaw).trim(), {
          isStoryRoute: false,
          preferLifeCampaign: false,
        });
        if (mapped?.data) {
          mapped.data.redirects = [{ primary_url: slug, is_story_campaign: false }];
          return mapped;
        }
      }
    }

    const matchingCampaign = resolveCampaignFromList(
      allCampaigns,
      normalizedSlug,
      slug,
      isStoryRoute,
      preferLifeCampaign
    );

    if (!matchingCampaign) {
      throw new Error("Campaign not found");
    }

    const apiSlug = getApiSlugForCampaign(
      matchingCampaign,
      normalizedSlug,
      isStoryRoute,
      preferLifeCampaign
    );
    const result = await fetchCampaignDetailBySlug(apiSlug, slugRouteOptions);
    return finalizeCampaignResult(result?.data, normalizedSlug, isStoryRoute);
  } catch (error) {
    throw error;
  }
};

// Replace the origin of a single URL with window.location.origin.
const toFrontendUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) return url;
    return window.location.origin + parsed.pathname + parsed.search + parsed.hash;
  } catch {}
  return url;
};

// Find any http(s) URLs embedded in a text string and fix their origin.
const fixUrlsInText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/https?:\/\/[^\s,)'"]+/g, (match) => {
    try {
      const parsed = new URL(match);
      if (parsed.origin !== window.location.origin) {
        return window.location.origin + parsed.pathname + parsed.search + parsed.hash;
      }
    } catch {}
    return match;
  });
};

export const getCampaignShareDetails = async (id) => {
  if (!id) {
    return null;
  }

  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.GET_CAMPAIGN_SHARE_DETAILS(id)
    );
    const data = response?.data?.data || response?.data || null;
    if (!data) return null;

    // Fix the copy/display URL — always use the current frontend origin.
    if (data.share_links?.copy) {
      data.share_links.copy = toFrontendUrl(data.share_links.copy);
    }
    if (data.link) {
      data.link = toFrontendUrl(data.link);
    }

    // Fix URLs embedded in the message text (used by WhatsApp and Twitter fallback builders).
    if (data.message) {
      data.message = fixUrlsInText(data.message);
    }

    // Drop the backend-generated social links — ShareLink.jsx fallback builders
    // will rebuild them correctly using the fixed copy URL and message above.
    const SOCIAL_KEYS = ['whatsapp', 'facebook', 'twitter', 'linkedin', 'gmail'];
    if (data.share_links) {
      SOCIAL_KEYS.forEach((k) => delete data.share_links[k]);
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export const createCampaign = async (campaignData) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.CREATE_CAMPAIGN, campaignData);
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateCampaign = async (id, campaignData) => {
  try {
    const response = await axiosInstance.put(API_ENDPOINTS.UPDATE_CAMPAIGN(id), campaignData);
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteCampaign = async (id) => {
  try {
    const response = await axiosInstance.delete(API_ENDPOINTS.DELETE_CAMPAIGN(id));
    return response;
  } catch (error) {
    throw error;
  }
};

// Start New Campaign (distinct workflow)
export const startNewCampaign = async (payload) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.START_CAMPAIGN, payload);
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Wishlist
export const getWishlist = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_WISHLIST);
    // Axios interceptor returns response.data, so response is already the API response body
    return response;
  } catch (error) {
    throw error;
  }
};

// Submit User Campaign Comment
export const submitUserCampaignComment = async (commentData) => {
  try {
    const formData = new FormData();
    formData.append('campaign_id', commentData.campaign_id);
    formData.append('user_id', commentData.user_id);
    formData.append('username', commentData.username);
    formData.append('comments', commentData.comments);
    formData.append('status', commentData.status || 0);
    
    if (commentData.profile_picture) {
      formData.append('profile_picture', commentData.profile_picture);
    }
    
    const response = await axiosInstance.post(API_ENDPOINTS.POST_USER_CAMPAIGN_COMMENT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get User Campaign Comments
export const getUserCampaignComments = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_USER_CAMPAIGN_COMMENTS);
    // Axios interceptor returns response.data, so response is already the API response body
    // Response is an array of comments
    return Array.isArray(response) ? response : [];
  } catch (error) {
    throw error;
  }
};
